import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GuestUserState } from './typings'
import { StoreData } from '../reducers/typings'
import { useAppDispatch } from '@user/store/hooks'
import { useCallback } from 'react'
import { getGuestUserLeads, getGuestUserOpportunities } from './api'
import {
  defaultGuestUserLeadFilters,
  defaultGuestUserOpportunityFilters,
} from '@user/store/guestUser/filters'
import { ILead, LeadFilterSet } from '@user/store/lead'
import { normalizeFilters } from '@user/components/Filters'
import { IOpportunity, OpportunityFilterSet } from '@user/store/opportunity'

// oppsInitialTotalCount is the unfiltered total, so the Opps tab is disabled
// only when truly no data exists.
export const initialState: GuestUserState = {
  leads: [],
  opportunities: [],
  leadsLoading: false,
  opportunitiesLoading: false,
  leadsError: null,
  opportunitiesError: null,
  leadsTotalCount: 0,
  opportunitiesTotalCount: 0,
  oppsInitialTotalCount: 0,
  leadsPage: 1,
  leadsPerPage: 10,
  leadsOrderBy: 'createdDate desc',
  leadsSearchText: '',
  leadsFilterSet: defaultGuestUserLeadFilters,
  oppsPage: 1,
  oppsPerPage: 10,
  oppsOrderBy: 'createdDate desc',
  oppsSearchText: '',
  oppsFilterSet: defaultGuestUserOpportunityFilters,
}

export const guestUserSlice = createSlice({
  name: 'guestUser',
  initialState,
  reducers: {
    setLeadsPage: (state, { payload }: PayloadAction<number>) => {
      state.leadsPage = payload
    },
    setLeadsPageSize: (state, { payload }: PayloadAction<number>) => {
      state.leadsPerPage = payload
    },
    setLeadsOrderBy: (state, { payload }: PayloadAction<string>) => {
      state.leadsOrderBy = payload
    },
    setLeadsSearch: (state, { payload }: PayloadAction<string>) => {
      state.leadsSearchText = payload
    },
    setLeadFilters: (state, { payload }: PayloadAction<LeadFilterSet>) => {
      state.leadsPage = 1
      state.leadsFilterSet = normalizeFilters<ILead, LeadFilterSet>(payload)
      state.leadsOrderBy = payload.orderBy
    },

    setOppsPage: (state, { payload }: PayloadAction<number>) => {
      state.oppsPage = payload
    },
    setOppsPageSize: (state, { payload }: PayloadAction<number>) => {
      state.oppsPerPage = payload
    },
    setOppsOrderBy: (state, { payload }: PayloadAction<string>) => {
      state.oppsOrderBy = payload
    },
    setOppsSearch: (state, { payload }: PayloadAction<string>) => {
      state.oppsSearchText = payload
    },
    setOppsFilters: (
      state,
      { payload }: PayloadAction<OpportunityFilterSet>,
    ) => {
      state.oppsPage = 1
      state.oppsFilterSet = normalizeFilters<
        IOpportunity,
        OpportunityFilterSet
      >(payload)
      state.oppsOrderBy = payload.orderBy
    },

    clearLeads: (state) => {
      state.leads = []
      state.leadsError = null
    },
    clearOpportunities: (state) => {
      state.opportunities = []
      state.opportunitiesError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGuestUserLeads.pending, (state) => {
        state.leadsLoading = true
        state.leadsError = null
      })
      .addCase(getGuestUserLeads.fulfilled, (state, action) => {
        state.leadsLoading = false
        state.leads = action.payload.data
        state.leadsTotalCount = action.payload.totalCount
        state.leadsError = null
      })
      .addCase(getGuestUserLeads.rejected, (state, action) => {
        state.leadsLoading = false
        state.leadsError = action.payload?.Message || 'Failed to load leads'
      })
      .addCase(getGuestUserOpportunities.pending, (state) => {
        state.opportunitiesLoading = true
        state.opportunitiesError = null
      })
      .addCase(getGuestUserOpportunities.fulfilled, (state, action) => {
        state.opportunitiesLoading = false
        state.opportunities = action.payload.data
        state.opportunitiesTotalCount = action.payload.totalCount
        state.opportunitiesError = null
        // Cache the unfiltered total (used to control tab disabled state)
        if (state.oppsInitialTotalCount === 0) {
          state.oppsInitialTotalCount = action.payload.totalCount
        }
      })
      .addCase(getGuestUserOpportunities.rejected, (state, action) => {
        state.opportunitiesLoading = false
        state.opportunitiesError =
          action.payload?.Message || 'Failed to load opportunities'
      })
  },
})

export const {
  setLeadsSearch,
  setOppsSearch,
  setLeadsPage,
  setLeadsPageSize,
  setLeadFilters,
  setOppsPage,
  setOppsPageSize,
  setOppsFilters,
} = guestUserSlice.actions

// Selectors
export const selectGuestUserLeads = (state: StoreData) => state.guestUser.leads
export const selectGuestUserOpportunities = (state: StoreData) =>
  state.guestUser.opportunities
export const selectGuestUserLeadsLoading = (state: StoreData) =>
  state.guestUser.leadsLoading
export const selectGuestUserOpportunitiesLoading = (state: StoreData) =>
  state.guestUser.opportunitiesLoading
export const selectGuestUserLeadsTotalCount = (state: StoreData) =>
  state.guestUser.leadsTotalCount
export const selectGuestUserOpportunitiesTotalCount = (state: StoreData) =>
  state.guestUser.opportunitiesTotalCount

export const selectGuestUserLeadsPage = (state: StoreData) =>
  state.guestUser.leadsPage
export const selectGuestUserLeadsPerPage = (state: StoreData) =>
  state.guestUser.leadsPerPage
export const selectGuestUserOppsPage = (state: StoreData) =>
  state.guestUser.oppsPage
export const selectGuestUserOppsPerPage = (state: StoreData) =>
  state.guestUser.oppsPerPage

// Guest User Preferences Selectors
export const selectGuestUserLeadsColumnOrder = (state: StoreData) => {
  // Load from user preferences
  return state.userPreferences.guestUserDashboardLeadsColumnOrder || []
}

export const selectGuestUserOppsColumnOrder = (state: StoreData) => {
  // Load from user preferences
  return state.userPreferences.guestUserDashboardOpportunitiesColumnOrder || []
}

// Default column visibility for Guest User Dashboard
export const getDefaultGuestUserLeadsColumnVisibility = () => ({
  // Show only the specified columns by default
  age: true,
  id: true, // Lead Created Date
  leadName: true,
  externalBillToCode: true, // Bill to Location Number
  externalLocationCode: true, // Service Location Number
  branch: true,
  salesTeamId: true, // Sales Team
  'assignee/firstName': true, // Owner
  salesFunnelStageId: true, // Status
  services: true,
  totalLeadValue: true, // Total Value
  targetPestIds: true, // Target Pests
  'createdBy/firstName': true,

  sourceId: false,
  campaignId: false,
  billToDivisionId: false,
  billToLocationTypeId: false,
  initialValue: false,
  recurringValue: false,
  referralLocationCode: false,
  referralDiscountCode: false,
  primaryContactSearchName: false,
  locationTypeId: false,
  divisionId: false,
  closedDate: false,
  'primaryContact/primaryEmailAddress': false,
  'primaryContact/phone': false,
  'primaryContact/address': false,
  type: false,
  disqualifiedReason: false,
  submittedBy: false,
  billToAddress: false,
  nextActionDate: false,
  salesFunnelId: false,
  address: false,
  primaryContactSearchPrimaryEmail: false,
  primaryContactSearchPhone: false,
  primaryContactSearchAddress: false,
})

export const getDefaultGuestUserOppsColumnVisibility = () => ({
  // Show only the specified columns by default
  age: true,
  createdDate: true, // Opportunity Created Date
  contactWithOpportunityName: true, // Opportunity Name
  externalBillToCode: true, // Bill to Location Number
  externalLocationCode: true, // Service Location Number
  branchId: true,
  salesTeamId: true, // Sales Team
  'assignee/firstName': true, // Owner
  services: true,
  totalValue: true, // Total Value
  targetPestIds: true, // Target Pests
  soldServiceStatus: true,
  'createdBy/firstName': true,
  leadCreatedBy: true,

  sourceId: false,
  campaignId: false,
  billToDivisionId: false,
  billToLocationTypeId: false,
  initialValue: false,
  recurringValue: false,
  referralLocationCode: false,
  referralDiscountCode: false,
  primaryContactSearchName: false,
  leadCreatedTime: false,
  divisionId: false,
  locationTypeId: false,
  nextActionDate: false,
  'primaryContact/primaryEmailAddress': false,
  'primaryContact/phone': false,
  'primaryContact/address': false,
  closedDate: false,
  type: false,
  closedLostReason: false,
  submittedBy: false,
  billToAddress: false,
  salesFunnelId: false,
  address: false,
  primaryContactSearchPrimaryEmail: false,
  primaryContactSearchPhone: false,
  primaryContactSearchAddress: false,
})

export const selectGuestUserLeadsFilters = (state: StoreData) => ({
  filterSet: {
    orderBy: state.guestUser.leadsOrderBy,
    search: state.guestUser.leadsSearchText,
  },
})

export const selectGuestUserLeadsFilterSet = (state: StoreData) =>
  state.guestUser.leadsFilterSet

export const selectGuestUserOppsFilterSet = (state: StoreData) =>
  state.guestUser.oppsFilterSet

export const selectGuestUserOppsFilters = (state: StoreData) => ({
  filterSet: {
    orderBy: state.guestUser.oppsOrderBy,
    search: state.guestUser.oppsSearchText,
  },
})

export const useSaveGuestUserLeadsFilters = () => {
  const dispatch = useAppDispatch()
  return useCallback(
    (filters: any) => {
      if (filters.orderBy) {
        dispatch(guestUserSlice.actions.setLeadsOrderBy(filters.orderBy))
      }
      if (filters.search !== undefined) {
        dispatch(guestUserSlice.actions.setLeadsSearch(filters.search))
      }
      // Trigger API call when filters change
      dispatch(getGuestUserLeads())
    },
    [dispatch],
  )
}

export const useSaveGuestUserLeadsFilterSet = () => {
  const dispatch = useAppDispatch()
  return useCallback(
    (filters: LeadFilterSet) => {
      dispatch(guestUserSlice.actions.setLeadFilters(filters))
      // Trigger API call when filters change
      dispatch(getGuestUserLeads())
    },
    [dispatch],
  )
}

export const useSaveGuestUserOppsFilters = () => {
  const dispatch = useAppDispatch()
  return useCallback(
    (filters: any) => {
      if (filters.orderBy) {
        dispatch(guestUserSlice.actions.setOppsOrderBy(filters.orderBy))
      }
      if (filters.search !== undefined) {
        dispatch(guestUserSlice.actions.setOppsSearch(filters.search))
      }
      // Trigger API call when filters change
      dispatch(getGuestUserOpportunities())
    },
    [dispatch],
  )
}

export const useSaveGuestUserOppsFilterSet = () => {
  const dispatch = useAppDispatch()
  return useCallback(
    (filters: OpportunityFilterSet) => {
      dispatch(guestUserSlice.actions.setOppsFilters(filters))
      // Trigger API call when filters change
      dispatch(getGuestUserOpportunities())
    },
    [dispatch],
  )
}

export default guestUserSlice.reducer
